import {
  Product,
  ProductFilters,
  ProductLookups,
  ProductModifiers,
} from './products.models';

export interface ProductsState {
  ids: string[];
  entities: Record<string, Product>;
  lookups: ProductLookups;
  filterOptions: ProductFilters;
  error: string | null;
  isLoaded: boolean;
  isLoading: boolean;
  isLoadedLookups: boolean;
  isLoadingLookups: boolean;
  modifiersError: string | null;
  lookupsError: string | null;
  lookupsSetError: string | null;
}

type Action =
  | {
      type: 'UPDATE_FILTER_OPTIONS';
      payload: { filterOptions: Partial<ProductFilters>; reset?: boolean };
    }
  | {
      type: 'FETCH_PRODUCTS';
      payload: { filterOptions: Partial<ProductFilters> };
    }
  | { type: 'FETCH_PRODUCTS_SUCCESS'; payload: { products: Product[] } }
  | { type: 'FETCH_PRODUCTS_FAILURE'; payload: { error: string } }
  | { type: 'FETCH_PRODUCTS_CANCELLED' }
  | { type: 'FETCH_PRODUCT'; payload: { pricebookId: string } }
  | { type: 'FETCH_PRODUCT_SUCCESS'; payload: { product: Product } }
  | { type: 'FETCH_PRODUCT_FAILURE'; payload: { error: string } }
  | { type: 'FETCH_PRODUCT_LOOKUPS' }
  | {
      type: 'FETCH_PRODUCT_LOOKUPS_SUCCESS';
      payload: { lookups: ProductLookups };
    }
  | { type: 'FETCH_PRODUCT_LOOKUPS_FAILURE'; payload: { error: string } }
  | { type: 'FETCH_PRODUCT_MODIFIERS' }
  | {
      type: 'FETCH_PRODUCT_MODIFIERS_SUCCESS';
      payload: { modifiers: ProductModifiers };
    }
  | { type: 'FETCH_PRODUCT_MODIFIERS_FAILURE'; payload: { error: string } }
  | { type: 'SET_PRODUCT_LOOKUPS' }
  | {
      type: 'SET_PRODUCT_LOOKUPS_SUCCESS';
      payload: { lookups: ProductLookups };
    }
  | { type: 'SET_PRODUCT_LOOKUPS_FAILURE'; payload: { error: string } };

export const initState: ProductsState = {
  ids: [],
  entities: {},
  lookups: { categories: [], brands: [], discounts: [], fees: [], rebates: [] },
  filterOptions: {
    sortOrder: 'desc',
    capacityMax: '',
    capacityMin: '',
    capacityUnit: '',
    efficiencyMax: '',
    efficiencyMin: '',
    efficiencyUnit: '',
    pageSize: 20,
    names: [],
    matches: [],
    pricebookTypes: [],
    categoryIds: [],
    brands: [],
    businessUnitIds: [],
    pricebookIds: [],
  },
  error: null,
  isLoaded: false,
  isLoading: false,
  isLoadedLookups: false,
  isLoadingLookups: false,
  modifiersError: null,
  lookupsError: null,
  lookupsSetError: null,
};

export const productsReducer = (
  state: ProductsState,
  action: Action
): ProductsState => {
  switch (action.type) {
    case 'UPDATE_FILTER_OPTIONS':
      return {
        ...state,
        ...(action.payload.reset && {
          ids: [],
          entities: {},
          error: null,
          isLoading: false,
          isLoaded: false,
        }),
        filterOptions: {
          ...state.filterOptions,
          ...action.payload.filterOptions,
        },
      };

    case 'FETCH_PRODUCTS':
      return {
        ...state,
        error: null,
        isLoading: true,
        isLoaded: !!state.ids.length,
        ids: [],
        entities: {},
        filterOptions: {
          ...state.filterOptions,
          ...action.payload.filterOptions,
          pageSize: initState.filterOptions.pageSize,
        },
      };

    case 'FETCH_PRODUCTS_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        ids: action.payload.products.map((a) => a.pricebookId),
        entities: action.payload.products.reduce(
          (prev, curr) => Object.assign(prev, { [curr.pricebookId]: curr }),
          {}
        ),
      };

    case 'FETCH_PRODUCT':
      return {
        ...state,
        error: null,
        isLoading: true,
        isLoaded: state.ids.includes(action.payload.pricebookId),
      };

    case 'FETCH_PRODUCT_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        ids: [...new Set(state.ids.concat(action.payload.product.pricebookId))],
        entities: {
          ...state.entities,
          [action.payload.product.pricebookId]: action.payload.product,
        },
      };

    case 'FETCH_PRODUCT_FAILURE':
    case 'FETCH_PRODUCTS_FAILURE':
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        error: action.payload.error,
      };

    case 'FETCH_PRODUCTS_CANCELLED':
      return {
        ...state,
        error: null,
        isLoaded: false,
      };

    case 'FETCH_PRODUCT_MODIFIERS':
    case 'FETCH_PRODUCT_LOOKUPS':
    case 'SET_PRODUCT_LOOKUPS':
      return {
        ...state,
        isLoadingLookups: !state.lookups.categories.length,
        isLoadedLookups: false,
        lookupsError:
          action.type === 'FETCH_PRODUCT_LOOKUPS' ? null : state.lookupsError,
        modifiersError:
          action.type === 'FETCH_PRODUCT_MODIFIERS'
            ? null
            : state.modifiersError,
        lookupsSetError:
          action.type === 'SET_PRODUCT_LOOKUPS' ? null : state.lookupsSetError,
      };

    case 'FETCH_PRODUCT_LOOKUPS_SUCCESS':
    case 'SET_PRODUCT_LOOKUPS_SUCCESS':
      return {
        ...state,
        isLoadingLookups: false,
        isLoadedLookups: true,
        lookupsError:
          action.type === 'FETCH_PRODUCT_LOOKUPS_SUCCESS'
            ? null
            : state.lookupsError,
        lookupsSetError:
          action.type === 'SET_PRODUCT_LOOKUPS_SUCCESS'
            ? null
            : state.lookupsSetError,
        lookups: {
          ...action.payload.lookups,
          categories: action.payload.lookups.categories
            .slice()
            .sort(
              (a, b) => a.position - b.position || a.name.localeCompare(b.name)
            ),
        },
      };

    case 'FETCH_PRODUCT_MODIFIERS_SUCCESS':
      return {
        ...state,
        isLoadingLookups: false,
        isLoadedLookups: true,
        lookupsError: null,
        modifiersError: null,
        lookups: {
          ...state.lookups,
          ...action.payload.modifiers,
        },
      };

    case 'SET_PRODUCT_LOOKUPS_FAILURE':
    case 'FETCH_PRODUCT_MODIFIERS_FAILURE':
    case 'FETCH_PRODUCT_LOOKUPS_FAILURE':
      return {
        ...state,
        isLoadingLookups: false,
        isLoadedLookups: true,
        lookupsError:
          action.type === 'FETCH_PRODUCT_LOOKUPS_FAILURE'
            ? action.payload.error
            : state.lookupsError,
        modifiersError:
          action.type === 'FETCH_PRODUCT_MODIFIERS_FAILURE'
            ? action.payload.error
            : state.modifiersError,
        lookupsSetError:
          action.type === 'SET_PRODUCT_LOOKUPS_FAILURE'
            ? action.payload.error
            : state.lookupsSetError,
      };

    default:
      return state;
  }
};
